import queryClient, { IQueryParams } from 'queries';
import helpers from 'helpers/index';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import type { TypedOrders } from './orders.query';

export interface TypedTicket {
    ticket_id?: string;
    order_id?: string;
    price?: number;
    discount?: number;
    ticket_type?: ENUM_TICKET_TYPE;
    /** đánh dấu là ghế ưu tiên ... */
    mark_as_priority?: number;
    seat_position?: string;
    createdAt?: string;
    updatedAt?: string;
    orders?: TypedOrders;
    markAsDeleted?: number;
}


export enum ENUM_TICKET_TYPE {
    PASSENGER = 'PASSENGER',
    SHIPMENT = 'SHIPMENT'
}

type IQuery = TypedTicket & IQueryParams;
type TypedUnassignedTicket = {
    departure_date: string;
    journey_id: string;
} & IQueryParams

type TypedDeletedTicket = {
    journey_id: string;
    trip_manifest_id: string;
} & IQueryParams

/*
* List of all ticket
*/
export function useGetTickets(object: IQuery) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["ticket/fetch_entity_list", object],
        queryFn: () => axios.get<TypedTicket[]>(`/ticket/${EndURL}`).then(response => response.data),
        retry: 1,
        enabled: !!object.order_id,
    });
}

export function useMutateUpdate_ticket_position() {
    return useMutation({
        mutationKey: ['ticket/update_position'],
        mutationFn: (body: { ticket_id: string, seat_position: string, trip_manifest_id: string | number | bigint }) =>
            axios.patch(`/ticket/update_position`, body).then(response => response.data),
    });
}

/** Remove a ticket from its trip manifest. */
export function useMutateRemove_ticket_from_trip() {
    return useMutation({
        mutationKey: ['ticket/delete_entity'],
        mutationFn: (ticket_id: string) =>
            axios.delete(`/ticket/unassign_ticket_from_trip_manifest/${ticket_id}`).then(response => response.data),
    });
}

/** Assign an existing ticket to a trip manifest. */
export function useMutateAdd_ticket_to_trip() {
    return useMutation({
        mutationKey: ['ticket/create_entity'],
        mutationFn: (body: { trip_manifest_id: string | number | bigint, ticket_id: string | bigint, seat_position?: string, mode?: string }) =>
            axios.post(`/ticket/assign_to_trip_manifest`, body).then(response => response.data),
    });
}

/** Move a ticket to another trip manifest. Chỉ phục vụ việc move thoai ... */
export function useMutateMoving_ticket_to_trip() {
    return useMutation({
        mutationKey: ['ticket/moving_entity'],
        mutationFn: (body: { trip_manifest_id: string | bigint, ticket_id: string | bigint, seat_position?: string }) => axios.post(`/ticket/moving_ticket_to_other_trip_manifest`, body).then(response => response.data),
    });
}


export function useGetUnassignedTickets(object: TypedUnassignedTicket) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["ticket/fetch_unassigned_list", object],
        queryFn: () => axios.get<TypedTicket[]>(`/ticket/unassigned_ticket_list/${EndURL}`).then(response => response.data),
        retry: 1,
        enabled: !!object,
    });
}

export function useGetDeletedTickets(object: TypedDeletedTicket) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["ticket/fetch_deleted_list"],
        queryFn: () => axios.get<TypedTicket[]>(`/ticket/deleted_ticket_list/${EndURL}`).then(response => response.data),
        retry: 1,
        enabled: !!object,
    });
}


export function useMutateGetTickets() {
    return useMutation({
        mutationKey: ['ticket/fetch_entity_list'],
        mutationFn: (object: IQuery) => axios.get<TypedTicket[]>(`/ticket/${helpers.buildEndUrl(object)}`).then((response) => response.data),
    })
};


/** Admin hủy vé, không phải xóa vé ... */
export function useMutateTicketTemporaryDelete() {
    return useMutation({
        mutationKey: ['ticket/admin_cancel'],
        mutationFn: (ticket_id: bigint | number | string) => axios.delete(`/ticket/temporary_delete/${ticket_id}`).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ticket/fetch_deleted_list'] });
        }
    });
}


/** Admin xóa vé, xóa vĩnh viễn ... */
export function useMutateTicketpermanentlyDelete() {
    return useMutation({
        mutationKey: ['ticket/admin_delete'],
        mutationFn: (ticket_id) => axios.delete(`/ticket/permanent_delete/${ticket_id}`).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ticket/fetch_deleted_list'] });
        }
    });
}

/** khôi phục vé đã xóa mềm */
export function useMutateTicketUndelete() {
    return useMutation({
        mutationKey: ['ticket/undelete'],
        mutationFn: (ticket_id: bigint | number | string) => axios.post(`/ticket/undelete_ticket`, { ticket_id }).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ticket/fetch_unassigned_list'] });
        }
    });
}



/** clone vé */
export function useMutateTicketClone() {
    return useMutation({
        mutationKey: ['ticket/admin_clone'],
        mutationFn: (ticket_id: bigint | number | string) => axios.post<TypedTicket>(`/ticket/clone/${ticket_id}`).then((response) => response.data),
        onSuccess: () => {
            // queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['ticket/fetch_unassigned_list'] }); // reset lại cái danh sách vé chưa điều chuyến
        }
    });
}



/** tách một vé ra khỏi một đơn, thành vé mới đơn mới */
export function useMutateTicketDetach() {
    return useMutation({
        mutationKey: ['ticket/admin_detach'],
        mutationFn: (ticket_id: any) => axios.post(`/ticket/detach/${ticket_id}`).then((response) => response.data),
        onSuccess: () => {
            /** giữ đây, không được động vào ... */
            queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
            queryClient.invalidateQueries({ queryKey: ['ticket/fetch_entity_list'] }); // reset lại cái danh sách vé để hiển thị
        }
    });
}


/** đánh dấu ghế ưu tiên */
export function useMutateTicketMarkAsPriority() {
    return useMutation({
        mutationKey: ['ticket/mark_as_priority'],
        mutationFn: (ticket_id: bigint | number | string) => axios.patch<any>(`/ticket/toggle_priority/${ticket_id}`).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['ticket/fetch_entity_list'] });
        }
    });
}
